/**
 * StartMenu.tsx — Sorry Boss front-facing start menu (BETA 22.50, UX v3)
 *
 * The "do it all" design pass:
 *  - V-FORMATION LINEUP: the selected survivor stands front + center, fully
 *    lit; neighbors recede behind in depth (smaller/darker/dimmer). LB-RB /
 *    arrow keys slide the whole lineup; locked survivors ride along as dark
 *    silhouettes. All 13 heroes stay mounted so the slide animates smoothly.
 *  - CAPTION STRIP: the big black card is dead. A borderless lower-third
 *    scrim carries STARTING WEAPON (lock glyph — it can never change) and
 *    PASSIVE (hex badge + numbers in purple). The name lives OVERHEAD.
 *  - MODE TABS, not doors: SOLO RUN is the home state; CO-OP 1-4 morphs this
 *    same screen into the lobby. Survivor choice survives mode switches.
 *  - MODE-AWARE START PLATE: one start control, one position. Red in solo,
 *    green + readiness count in co-op, gray + unlock requirement on locked
 *    survivors.
 *  - Rail regrouped by intent: Play / Prepare / System, with dividers.
 *
 * Fixed 1600×900 design stage, uniformly scaled — no reflow at any size.
 * TODO(gamepad): controller focus nav (hold-A start, Y inspect) — the input
 * map is designed (see lobby UX doc); DOM wiring is the next pass.
 */
import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { resolveAsset } from '../game/uiAssets';
import { CHANGELOG, CURRENT_VERSION } from '../game/CHANGELOG';
import { CHARACTER_PASSIVES } from '../game/characterPassives';
import { getMenuLiveStats } from '../game/menuLiveData';
import { isCharacterUnlocked } from '../game/loadoutSelection';
import { playMenuSound, preloadMenuSounds, startMenuAmbience, stopMenuAmbience } from '../game/audio';
import { readPadDirection, getActiveScreen } from '../game/gamepadFocus';
import FloatingEmbers from './FloatingEmbers';

// BETA 22.51 — Dylan (video feedback): "for now make all the characters
// unlocked ... I'm a dev playing so I need everything in the game unlocked."
// Menu-side override only (quests/unlock data untouched) — flip to false when
// the dev period ends and real unlock gating returns.
const ALL_CHARACTERS_UNLOCKED = true;
const charUnlocked = (id: string) => ALL_CHARACTERS_UNLOCKED || isCharacterUnlocked(id);

const DW = 1600, DH = 900;
const MONO = "'Share Tech Mono', monospace";
const HEAVY = "'Anton', 'Archivo Black', 'Arial Black', sans-serif";
const MENU_BG = resolveAsset('/menu/menu_bg_main.jpg');
// Dylan's transparent SORRY BOSS wordmark ("sorry boss logo.png") — big and
// centered during the boot entrance, then parked bottom-left as a sticker.
const SB_LOGO = resolveAsset('/menu/sorry_boss_logo.png');
const PURPLE = '#b44dff';

const NOISE =
  'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'120\' height=\'120\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'2\'/%3E%3CfeColorMatrix type=\'saturate\' values=\'0\'/%3E%3C/filter%3E%3Crect width=\'120\' height=\'120\' filter=\'url(%23n)\' opacity=\'0.55\'/%3E%3C/svg%3E")';

// -- Roster (13 heroes). gameIdx = index into Home.tsx CHARACTERS. unlockReq
//    mirrors the REAL quest conditions (QuestsScreen CHAR_QUESTS). -----------
type Hero = { id: string; name: string; weapon: string; weaponUrl: string; gameIdx: number; unlockReq: string };
const ROSTER: Hero[] = [
  { id: 'killa',       name: 'DUCK',           weapon: 'RPK Machine Gun',     weaponUrl: '/weapons/wpn_rpk_solid.png',  gameIdx: 3,  unlockReq: 'Kill 250 enemies total' },
  { id: 'tactical',    name: 'MILITARY GUY',   weapon: '.50 Cal Sniper',      weaponUrl: '/weapons/wpn_peanut_rifle_solid.png',     gameIdx: 0,  unlockReq: '' },
  { id: 'sponge',      name: 'FAT GUY',        weapon: 'Barbed Wire Bat',     weaponUrl: '/weapons/wpn_barbed_bat_solid.png',    gameIdx: 1,  unlockReq: '' },
  { id: 'cowboy',      name: 'COWBOY',         weapon: 'The Revolver',        weaponUrl: '/weapons/weapon_03_revolver.png',         gameIdx: 2,  unlockReq: '' },
  { id: 'wizard',      name: 'ROBOT GIRL',     weapon: 'Firestarter',         weaponUrl: '/weapons/wpn_flamethrower_solid.png',    gameIdx: 4,  unlockReq: 'Survive 10 min in a single run' },
  { id: 'viking',      name: 'VIKING',         weapon: 'Viking Axe',          weaponUrl: '/weapons/wpn_viking_axe_solid.png',       gameIdx: 5,  unlockReq: 'Kill 3 bosses total' },
  { id: 'mr_silly',    name: 'GEN-Z COOLKID',  weapon: 'Beer Grenade',        weaponUrl: '/weapons/wpn_beer_grenade_solid.png',     gameIdx: 6,  unlockReq: 'Complete 5 runs' },
  { id: 'lebron',      name: 'SMOKESHOW',      weapon: 'Dodge This',          weaponUrl: '/weapons/wpn_dodgeball_solid.png', gameIdx: 7, unlockReq: 'Reach level 15 in a single run' },
  { id: 'chad',        name: 'BUFF GUY',       weapon: 'Personal Space',      weaponUrl: '/weapons/weapon_09_chad_aura.png',        gameIdx: 8,  unlockReq: 'Collect 2,500 gold total' },
  { id: 'greg',        name: 'GREG',           weapon: 'Karen Drones',        weaponUrl: '/weapons/wpn_karen_drones.png',           gameIdx: 10, unlockReq: 'Unlock quest coming soon' },
  { id: 'suit_guy',    name: 'SUIT GUY',       weapon: 'The Ex Girlfriend',   weaponUrl: '/weapons/wpn_ex_girlfriend_solid.png',          gameIdx: 11, unlockReq: 'Unlock quest coming soon' },
  { id: 'cheerleader', name: 'CHEERLEADER',    weapon: 'Squirter',            weaponUrl: '/weapons/wpn_squirter.png',               gameIdx: 12, unlockReq: 'Unlock quest coming soon' },
  { id: 'bigfoot',     name: 'BIGFOOT',        weapon: 'Banana Bomb',         weaponUrl: '/weapons/wpn_banana_bomb_solid.png',      gameIdx: 13, unlockReq: 'Unlock quest coming soon' },
];
const N = ROSTER.length;
// The boot sweep lands on GREG (Dylan's pick) — he's also the default
// selection so straight boots (intro already seen) match the choreography.
const BOOT_IDX = Math.max(0, ROSTER.findIndex(c => c.id === 'greg'));

// -- Inline stroke icons (currentColor) ---------------------------------------
const Icon = ({ d, fill = false, size = 26 }: { d: string; fill?: boolean; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill ? 'currentColor' : 'none'}
    stroke="currentColor" strokeWidth={fill ? 0 : 2.2} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} fillRule="evenodd" />
  </svg>
);
const ICONS = {
  // BETA 22.51 — Dylan: "that does not look like a skull by the way." Real
  // skull silhouette: dome, jaw with two tooth gaps, round eye sockets +
  // nose punched out via evenodd.
  skull: 'M12 1.5a8.5 8.5 0 0 1 8.5 8.5c0 2.7-1.25 4.8-3.2 6.1V21h-2.5v-2.2h-1.55V21h-2.5v-2.2H9.2V21H6.7v-4.9C4.75 14.8 3.5 12.7 3.5 10A8.5 8.5 0 0 1 12 1.5zM8.7 8.4a2.15 2.15 0 1 1 0 4.3 2.15 2.15 0 0 1 0-4.3zm6.6 0a2.15 2.15 0 1 1 0 4.3 2.15 2.15 0 0 1 0-4.3zM12 12.1l1.5 2.6h-3z',
  solo: 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1',
  users: 'M16 20v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 10a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM22 20v-2a4 4 0 0 0-3-3.9M16 3.1a3.5 3.5 0 0 1 0 6.8',
  quests: 'M9 11l3 3 8-8M20 12v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h11',
  wrench: 'M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.5-.7-.7-2.5 2.5-2.5z',
  stash: 'M3 8l9-4 9 4v8l-9 4-9-4V8zM3 8l9 4 9-4M12 12v8',
  cart: 'M2 3h2l2.4 12.4a2 2 0 0 0 2 1.6h9.2a2 2 0 0 0 2-1.6L23 6H6M9 21a1 1 0 1 0 .01 0M18 21a1 1 0 1 0 .01 0',
  gear: 'M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zM19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-2.7-1.1l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.1-2.7l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z',
  power: 'M12 3v9M6.4 6.4a8 8 0 1 0 11.2 0',
  chat: 'M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2v10z',
  lock: 'M5 11h14v10H5zM8 11V7a4 4 0 0 1 8 0v4',
};

// Rail: Play / Prepare / System (grouped by intent, hairline dividers).
const NAV_PREP = [
  { key: 'modbench', label: 'MOD BENCH', icon: ICONS.wrench },
  { key: 'stash',    label: 'STASH',     icon: ICONS.stash },
  { key: 'quests',   label: 'QUESTS',    icon: ICONS.quests },
  { key: 'store',    label: 'STORE',     icon: ICONS.cart },
];
const NAV_SYS = [
  { key: 'settings', label: 'SETTINGS', icon: ICONS.gear },
  { key: 'quit',     label: 'QUIT',     icon: ICONS.power },
];

const PLATE_BG =
  'radial-gradient(ellipse 80% 60% at 20% 110%, rgba(0,0,0,0.5), transparent 60%),' +
  'radial-gradient(ellipse 50% 40% at 85% -10%, rgba(120,135,155,0.12), transparent 60%),' +
  'linear-gradient(180deg, #262b33 0%, #191d24 45%, #0f1218 100%)';
const PLATE_SHADOW = (hov: boolean) =>
  (hov ? '0 0 16px rgba(255,106,42,0.30), ' : '') +
  'inset 0 2px 0 rgba(200,215,235,0.10), inset 0 -3px 6px rgba(0,0,0,0.55), 0 4px 10px rgba(0,0,0,0.5)';

const Screw = ({ x, y }: { x: number; y: number }) => (
  <span style={{ position: 'absolute', left: x, top: y, width: 6, height: 6, borderRadius: '50%',
    background: 'radial-gradient(circle at 35% 30%, #9aa5b5, #2b313b 70%)', boxShadow: '0 1px 1px rgba(0,0,0,0.8)', pointerEvents: 'none' }} />
);
const Grime = ({ radius = 8, opacity = 0.10 }: { radius?: number; opacity?: number }) => (
  <span style={{ position: 'absolute', inset: 0, borderRadius: radius, backgroundImage: NOISE,
    opacity, pointerEvents: 'none', mixBlendMode: 'overlay' }} />
);

// Depth styling for the V-lineup by signed distance from the selected slot.
// BETA 22.51 — Dylan (video feedback): "only three characters ... instead
// could it be a character here, here, here ... four, smaller and smaller."
// 4 visible on EACH side of the selected survivor (9 on stage of 13).
// BETA 22.51b — Dylan (10.42 PM video): "they look like they're floating —
// lower so their feet are closer to the ground" and "instead of fading them
// just make them darker, the further back the darker." So: visible survivors
// are FULLY OPAQUE (only the off-stage rung fades for the slide-in), depth is
// carried by brightness alone, and the per-step lift is tiny (see `bottom`).
function lineupStyle(d: number): { x: number; scale: number; bright: number; op: number; z: number } {
  const a = Math.abs(d);
  const s = d > 0 ? 1 : -1;   // x below is the TOTAL offset at that depth, not a per-step multiplier
  if (a === 0) return { x: 0,       scale: 1.0,  bright: 1.0,  op: 1, z: 30 };
  if (a === 1) return { x: s * 205, scale: 0.66, bright: 0.55, op: 1, z: 24 };
  if (a === 2) return { x: s * 350, scale: 0.53, bright: 0.38, op: 1, z: 18 };
  if (a === 3) return { x: s * 468, scale: 0.43, bright: 0.26, op: 1, z: 12 };
  if (a === 4) return { x: s * 566, scale: 0.35, bright: 0.17, op: 1, z: 6 };
  return        { x: s * 640,       scale: 0.3,  bright: 0.12, op: 0, z: 0 };
}

interface Props {
  onStartGame?: (charIdx: number) => void;
  onOpenSettings?: () => void;
  onOpenLoadout?: () => void;   // → MOD BENCH
  onOpenQuests?: () => void;
  onSignOut?: () => void;       // only passed when signed in — renders SIGN OUT in the Discord/News row
  signedIn?: boolean;           // BETA 22.52 — gates START RUN (browsing is always free)
  onRequestSignIn?: (charIdx?: number) => void;  // raises the sign-in overlay
  username?: string;
  inputLocked?: boolean;        // true while the boot intro overlays the menu
}

// Boot-entrance phases (Dylan's rip design). 'hold' = under the intro overlay:
// SOLID BLACK behind the centered SORRY BOSS logo. The tear reveals that →
// 'fade' (black fades into the full-color zoomed-in background) → 'zoom'
// (starts only once color is showing: bg zooms out WHILE the logo flies to
// the bottom-right — both land at the exact same moment; "the background
// should only be full screen right as that logo lands") → 'sweep' (lineup
// blurs through the roster onto DUCK) → 'snap' (smoke) → 'slam' (UI drops
// top-down) → 'done'.
type EntPhase = 'hold' | 'fade' | 'zoom' | 'sweep' | 'snap' | 'slam' | 'done';

export default function StartMenu({ onStartGame, onOpenSettings, onOpenLoadout, onOpenQuests, onSignOut, signedIn, onRequestSignIn, username, inputLocked }: Props) {
  const [idx, setIdx] = useState(BOOT_IDX);
  const [scale, setScale] = useState(1);        // CONTAIN scale — scene AND UI (22.88: whole menu is a letterboxed 16:9 stage)
  const [toast, setToast] = useState<string | null>(null);
  const [hoverNav, setHoverNav] = useState<string | null>(null);
  const [showChangelog, setShowChangelog] = useState(false);
  const [view, setView] = useState<'home' | 'lobby'>('home');
  const [p1Ready, setP1Ready] = useState(false);
  // Entrance only runs when the boot intro is overlaying us at mount;
  // straight boots (intro already seen / bot runs) start fully settled.
  const [entPhase, setEntPhase] = useState<EntPhase>(() => {
    if (inputLocked) return 'hold';                       // boot intro is overlaying us
    try {                                                 // fresh-boot sign-in path: the tear
      if (sessionStorage.getItem('sb_entrance_pending') === '1') return 'hold';
    } catch { /* */ }                                     // revealed AUTH, menu mounts later
    return 'done';
  });
  // 22.52b — the logo's corner flight is its own beat now (Dylan: it stays
  // centered while the survivors scroll behind it, then SLAPS into the corner
  // ~2s into the sweep — "too much empty space" when it left with the zoom).
  const [logoFly, setLogoFly] = useState(false);
  const [puffs, setPuffs] = useState(false);
  // Live values for the entrance timers (runEntrance closes over nothing).
  const signedInRef = useRef(signedIn);
  const requestSignInRef = useRef(onRequestSignIn);
  useEffect(() => { signedInRef.current = signedIn; requestSignInRef.current = onRequestSignIn; });
  const entTimers = useRef<number[]>([]);
  const prevLockedRef = useRef(inputLocked);
  const wrapRef = useRef<HTMLDivElement>(null);
  const hero = ROSTER[idx];
  const heroUnlocked = charUnlocked(hero.id);

  useEffect(() => {
    const fit = () => {
      const el = wrapRef.current; if (!el) return;
      const s = Math.min(el.clientWidth / DW, el.clientHeight / DH);
      setScale(s > 0 ? s : 1);
    };
    fit();
    const ro = new ResizeObserver(fit);
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener('resize', fit);
    const ivl = window.setInterval(fit, 200);
    const stop = window.setTimeout(() => window.clearInterval(ivl), 2000);
    return () => { ro.disconnect(); window.removeEventListener('resize', fit); window.clearInterval(ivl); window.clearTimeout(stop); };
  }, []);

  // ── Menu Feel v1: warm the UI sound kit + run the world ambience ──────
  // Ambience starts when the world becomes visible ('fade' during the boot
  // entrance, immediately on straight boots) and dies with the menu.
  useEffect(() => { preloadMenuSounds(); }, []);
  useEffect(() => {
    if (entPhase === 'fade' || entPhase === 'done') startMenuAmbience();
  }, [entPhase]);
  useEffect(() => () => stopMenuAmbience(), []);

  // ── Boot entrance timeline ─────────────────────────────────────────────
  // Two triggers, one choreography:
  //  A) the intro overlay lifts while we're mounted (inputLocked true→false)
  //  B) we mount fresh with the one-shot sb_entrance_pending flag (fresh app
  //     boot: the tear revealed the SIGN-IN screen; the menu mounts only
  //     after auth, so the entrance plays now instead of being swallowed).
  const runEntrance = useCallback(() => {
    try { sessionStorage.removeItem('sb_entrance_pending'); } catch { /* */ }
    const T = (fn: () => void, ms: number) => entTimers.current.push(window.setTimeout(fn, ms));
    setEntPhase('fade');                           // black → full-color bg (1.1s CSS fade)
    let t = 650;                                   // zoom begins once color is clearly showing
    T(() => setEntPhase('zoom'), t);               // bg 1.6→1 over 1.6s — logo HOLDS center
    t += 450;                                      // survivors roll in DURING the zoom (Dylan: "sooner")
    T(() => setEntPhase('sweep'), t);
    const sweepStart = t;
    // TWO full circles of the roster (Dylan: "faster — doesn't matter if you
    // pass the duck multiple times"): 26 steps left→right, flat-out fast,
    // then a short slot-machine decel that lands back on DUCK.
    const gaps = [
      ...Array.from({ length: 15 }, () => 52),     // blur through the first lap+
      58, 64, 72, 84, 100, 122, 150, 185, 230, 285, 350,
    ];
    gaps.forEach(g => { t += g; T(() => { setIdx(i => (i - 1 + N) % N); playMenuSound('ui_carousel_tick', 0.3); }, t); });
    // ~2s into the scroll the logo SLAPS itself into the corner (keyframed
    // fling — rear back, hurl, squash on impact); stamp sound on the hit.
    T(() => setLogoFly(true), sweepStart + 2000);
    T(() => playMenuSound('ui_logo_stamp', 0.75, 0.5), sweepStart + 2400);
    t += 220;                                      // let the last slide settle
    T(() => { setEntPhase('snap'); setPuffs(true); playMenuSound('ui_carousel_stop', 0.7); playMenuSound('ui_smoke_slam', 0.85); }, t);
    T(() => { setEntPhase('slam'); playMenuSound('ui_slam_down', 0.8); }, t + 430);
    T(() => { setEntPhase('done'); setPuffs(false); }, t + 1400);
    // Dylan: "1 second after the entire animation of the UI slamming down,
    // the sign-in pops up" — only when signed out, only on the boot entrance.
    T(() => {
      if (!signedInRef.current && requestSignInRef.current) requestSignInRef.current();
    }, t + 2400);
  }, []);
  // Trigger A: intro overlay lifts above us.
  useEffect(() => {
    const was = prevLockedRef.current;
    prevLockedRef.current = inputLocked;
    if (was && !inputLocked && entPhase === 'hold') runEntrance();
  }, [inputLocked, entPhase, runEntrance]);
  // Trigger B: mounted post-auth holding the pending flag (no overlay above).
  useEffect(() => {
    if (!inputLocked && entPhase === 'hold') runEntrance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => () => entTimers.current.forEach(id => window.clearTimeout(id)), []);

  // Any key/click during the entrance skips straight to the settled menu.
  // BETA 22.74 — extracted so the gamepad poller can skip too (any button).
  const skipEntrance = useCallback(() => {
    entTimers.current.forEach(id => window.clearTimeout(id));
    entTimers.current = [];
    setIdx(BOOT_IDX); setPuffs(false); setLogoFly(true); setEntPhase('done');
  }, []);
  useEffect(() => {
    if (entPhase === 'done' || entPhase === 'hold') return;
    window.addEventListener('keydown', skipEntrance);
    window.addEventListener('pointerdown', skipEntrance);
    return () => { window.removeEventListener('keydown', skipEntrance); window.removeEventListener('pointerdown', skipEntrance); };
  }, [entPhase, skipEntrance]);

  // A fresh player should never boot onto a LOCKED survivor. If the default
  // selection is locked, hop to the first unlocked one (re-checked shortly
  // after mount because unlock data streams in from Home's goopState effect).
  // Once the player cycles manually, never auto-hop again.
  const touchedRef = useRef(false);

  // BETA 22.63 — Dylan: solo needs a LOCK IN step. Clicking LOCK IN swaps the
  // centered survivor to their SECONDARY POSE (thumbs up — "I'm picked") and
  // START RUN requires it. Cycling always unlocks.
  const [lockedIn, setLockedIn] = useState(false);

  const cycle = useCallback((step: number) => {
    touchedRef.current = true;
    setIdx(i => (i + step + N) % N);
    setP1Ready(false);   // changing survivor always drops ready (design invariant)
    setLockedIn(false);  // changing survivor always drops the lock-in
    playMenuSound('ui_carousel_tick', 0.45);   // the weapon rack advances one detent
  }, []);
  useEffect(() => {
    const fix = () => {
      if (touchedRef.current) return;
      setIdx(i => {
        if (charUnlocked(ROSTER[i].id)) return i;
        const first = ROSTER.findIndex(c => charUnlocked(c.id));
        return first >= 0 ? first : i;
      });
    };
    fix();
    const t = window.setTimeout(fix, 700);
    return () => window.clearTimeout(t);
  }, []);

  const say = useCallback((msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(t => (t === msg ? null : t)), 1900);
  }, []);

  // BETA 22.74 — shared action callbacks so mouse, keyboard, and GAMEPAD all
  // funnel through identical logic (same sounds, same invariants).
  const toggleLockIn = useCallback(() => {
    if (lockedIn) { setLockedIn(false); playMenuSound('ui_back', 0.5); return; }
    setLockedIn(true); playMenuSound('ui_ready', 0.7);
  }, [lockedIn]);
  const toggleReady = useCallback(() => {
    playMenuSound(p1Ready ? 'ui_unready' : 'ui_ready', 0.65);
    setP1Ready(r => !r);
  }, [p1Ready]);

  const tryStart = useCallback(() => {
    if (!heroUnlocked) { playMenuSound('ui_denied', 0.6); say(`LOCKED — ${hero.unlockReq || 'keep playing to unlock'}`); return; }
    if (view === 'home' && !lockedIn) { playMenuSound('ui_denied', 0.6); say('LOCK IN YOUR SURVIVOR FIRST'); return; }
    if (view === 'lobby' && !p1Ready) { playMenuSound('ui_denied', 0.6); say('READY UP FIRST'); return; }
    // BETA 22.52 — identity only when it matters: signed out + START RUN →
    // raise the sign-in overlay; the run launches right after auth.
    if (!signedIn && onRequestSignIn) { playMenuSound('ui_select', 0.5); onRequestSignIn(hero.gameIdx); return; }
    playMenuSound('ui_confirm', 0.9);   // the breaker lever slams — we're going
    onStartGame?.(hero.gameIdx);
  }, [heroUnlocked, hero, view, p1Ready, lockedIn, signedIn, onRequestSignIn, onStartGame, say]);

  // Keyboard: ← → cycle, Enter starts. Disabled while the intro overlays us
  // and during the boot entrance (the skip handler owns input until 'done').
  useEffect(() => {
    if (inputLocked || entPhase !== 'done') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') cycle(-1);
      else if (e.key === 'ArrowRight') cycle(1);
      else if (e.key === 'Enter') tryStart();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cycle, tryStart, inputLocked, entPhase]);

  // ── BETA 22.74 — GAMEPAD on the DOM menu (Dylan plays on ROG Ally; the
  // menu had keyboard-only nav since the canvas menu died in 22.5x, so a
  // controller-only player literally could not lock in or start a run).
  // Mapping (critical path only — ModBench/Quests/etc = follow-up pass):
  //   d-pad / either stick / LB / RB ... cycle survivor (the LB/RB pills
  //                                      already advertised this)
  //   A .......... context advance: LOCK IN → START (lobby: READY → START)
  //   B .......... unlock / leave lobby view
  //   Start ...... tryStart (self-guards with the LOCK IN toast)
  //   any button . skips the boot entrance (parity with keydown)
  // All state the loop reads lives in refs — the rAF loop mounts ONCE and a
  // held button can never re-fire across the state change it caused.
  const padStateRef = useRef({ prev: new Map<number, boolean>(), dirSince: 0, lastStep: 0, lastDir: null as string | null });
  const gpRef = useRef({ view, lockedIn, p1Ready, entPhase, inputLocked, heroUnlocked });
  useEffect(() => { gpRef.current = { view, lockedIn, p1Ready, entPhase, inputLocked, heroUnlocked }; });
  const gpActionsRef = useRef({ cycle, tryStart, toggleLockIn, toggleReady, skipEntrance, setView });
  useEffect(() => { gpActionsRef.current = { cycle, tryStart, toggleLockIn, toggleReady, skipEntrance, setView }; });
  useEffect(() => {
    const INITIAL_REPEAT_MS = 280, REPEAT_MS = 140;
    let raf = 0;
    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      const pad = navigator.getGamepads?.()?.find(p => p && p.connected) ?? null;
      const ps = padStateRef.current;
      const snapshotButtons = () => {
        if (!pad) return;
        for (let i = 0; i < pad.buttons.length; i++) ps.prev.set(i, !!pad.buttons[i]?.pressed);
      };
      if (!pad) { ps.prev.clear(); ps.lastDir = null; return; }
      const g = gpRef.current;
      const act = gpActionsRef.current;
      // Gate 1: intro overlay owns input entirely.
      if (g.inputLocked) { snapshotButtons(); ps.lastDir = null; return; }
      // Gate 2: a registered focus screen (Settings/Quests/Auth overlays,
      // in-game screens) owns the pad — never fight installMenuGamepadTicker.
      if (getActiveScreen() !== null) { snapshotButtons(); ps.lastDir = null; return; }
      const rose = (i: number) => !!pad.buttons[i]?.pressed && ps.prev.get(i) !== true;
      // Gate 3: during the boot entrance any rising edge = skip (keydown parity).
      if (g.entPhase !== 'done') {
        for (let i = 0; i < pad.buttons.length; i++) {
          if (rose(i)) { act.skipEntrance(); break; }
        }
        snapshotButtons(); ps.lastDir = null; return;
      }
      // Direction (shared decoder — includes the Ally d-pad-as-axis quirk)
      // with hold-repeat: first step immediate, then 280ms, then 140ms.
      const dir = readPadDirection(pad);
      if (dir === 'left' || dir === 'right') {
        const fresh = ps.lastDir !== dir;
        const wait = fresh ? 0 : (now - ps.dirSince < INITIAL_REPEAT_MS + 60 ? INITIAL_REPEAT_MS : REPEAT_MS);
        if (fresh || now - ps.lastStep >= wait) {
          act.cycle(dir === 'left' ? -1 : 1);
          ps.lastStep = now;
          if (fresh) ps.dirSince = now;
        }
        ps.lastDir = dir;
      } else {
        ps.lastDir = null;
      }
      if (rose(4)) act.cycle(-1);   // LB
      if (rose(5)) act.cycle(1);    // RB
      if (rose(0)) {                // A — context advance
        if (g.view === 'home') {
          if (!g.heroUnlocked) act.tryStart();          // → LOCKED toast feedback
          else if (!g.lockedIn) act.toggleLockIn();
          else act.tryStart();
        } else {
          if (!g.p1Ready) act.toggleReady();
          else act.tryStart();
        }
      }
      if (rose(9)) act.tryStart();  // Start — self-guarded, toast onboarding
      if (rose(1)) {                // B — back out one commitment
        if (g.view === 'home') { if (g.lockedIn) act.toggleLockIn(); }
        else if (g.p1Ready) act.toggleReady();
        else act.setView('home');
      }
      snapshotButtons();
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const comingSoon = (label: string) => say(label + ' — coming soon');

  const onNav = (key: string) => {
    playMenuSound('ui_select', 0.5);   // cockpit toggle CLACK
    if (key === 'modbench') { onOpenLoadout?.(); return; }
    if (key === 'quests')   { onOpenQuests?.(); return; }
    if (key === 'settings') { onOpenSettings?.(); return; }
    if (key === 'quit') {
      window.close();
      window.setTimeout(() => comingSoon('QUIT: close the window'), 60);
      return;
    }
    comingSoon(key.toUpperCase());
  };

  const weaponSrc = useMemo(() => resolveAsset(hero.weaponUrl), [hero.weaponUrl]);
  const heroSrc = useMemo(() => resolveAsset(`/characters/hero/${hero.id}.png`), [hero.id]);
  useEffect(() => { try { localStorage.setItem('sb_menu_char', hero.id); } catch { /* */ } }, [hero.id]);
  const pass = CHARACTER_PASSIVES[hero.id];
  const passDesc = (pass?.signatureDesc ?? 'No signature yet.').split(/(\+?\d+(?:\.\d+)?(?:%|×|x)?)/g);
  const stats = getMenuLiveStats();

  // ── Entrance-aware render helpers ──────────────────────────────────────
  const uiVisible = entPhase === 'slam' || entPhase === 'done';
  const lineupVisible = entPhase === 'sweep' || entPhase === 'snap' || uiVisible;
  // Logo stays big + centered through hold/fade/zoom AND the first ~2s of
  // the survivor sweep — it only leaves when logoFly fires (its own beat).
  const logoBig = entPhase !== 'done' && !logoFly;
  const bgZoomed = entPhase === 'hold' || entPhase === 'fade';
  // Hidden until the slam, then each group drops in with a small stagger.
  const slam = (delay: number): React.CSSProperties =>
    !uiVisible ? { opacity: 0, pointerEvents: 'none' }
      : entPhase === 'slam' ? { animation: `ui_slam 0.42s cubic-bezier(0.25,1.3,0.45,1) ${delay}ms both` }
        : {};

  // Mode-aware start plate content.
  const plateDisabled = !heroUnlocked || (view === 'lobby' && !p1Ready);
  const plateColors = !heroUnlocked
    ? { face: 'linear-gradient(180deg, #3a3f47 0%, #262a31 55%, #16181d 100%)', frame: '#101318', text: '#8b93a1', sub: hero.unlockReq || 'locked' }
    : view === 'lobby'
      ? { face: 'linear-gradient(180deg, #8fbc35 0%, #6f9428 55%, #4c661a 100%)', frame: '#1c2608', text: '#111a05', sub: p1Ready ? '1/1 READY' : '0/1 READY — press READY UP' }
      : { face: 'radial-gradient(ellipse 60% 90% at 78% 115%, rgba(60,4,2,0.65), transparent 60%), radial-gradient(ellipse 45% 70% at 12% -15%, rgba(255,120,95,0.35), transparent 55%), linear-gradient(180deg, #d43527 0%, #b01e15 52%, #7c100c 100%)', frame: '#16100d', text: '#140302', sub: 'SOLO' };

  return (
    <div ref={wrapRef} className="sbmenu" style={{ position: 'fixed', inset: 0, background: '#000', overflow: 'hidden' }}>
      {/* ═══ SCENE STAGE (BETA 22.88 — Dylan: "start menu is not 16:9…") ═══
          The 22.53b cover-fill is GONE: the scene now uses the same CONTAIN
          scale as the UI, clipped to the 16:9 box, with true black bars
          outside — the menu behaves exactly like the letterboxed game canvas
          at every window size. The entrance zoom (×1.6 → ×1) happens INSIDE
          the clip box, so it never spills into the bars. */}
      <div style={{ position: 'absolute', left: '50%', top: '50%',
        width: DW * scale, height: DH * scale,
        transform: 'translate(-50%, -50%)', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: '50%', top: '50%', width: DW, height: DH,
        transform: `translate(-50%, -50%) scale(${scale * (bgZoomed ? 1.6 : 1)})`,
        transition: entPhase === 'zoom' || entPhase === 'sweep'
          ? 'transform 1.6s cubic-bezier(0.3,0.6,0.25,1)' : 'none' }}>

        {/* ══ Backdrop: monster-army plate + alive ambient layers ══ */}
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          <img src={MENU_BG} alt="" style={{
            position: 'absolute', left: '-3%', top: '-3%', width: '106%', height: '106%',
            objectFit: 'cover', animation: 'bg_drift 46s ease-in-out infinite alternate' }} />
          {[
            { x: 120,  y: 552, r: 150, dur: 4.6 }, { x: 472,  y: 516, r: 115, dur: 6.1 },
            { x: 800,  y: 512, r: 110, dur: 5.4 }, { x: 1120, y: 516, r: 115, dur: 7.0 },
            { x: 1484, y: 548, r: 150, dur: 5.2 },
          ].map((g, i) => (
            <div key={i} style={{ position: 'absolute', left: g.x - g.r, top: g.y - g.r, width: g.r * 2, height: g.r * 2,
              background: 'radial-gradient(circle, rgba(255,125,22,0.55) 0%, transparent 68%)',
              mixBlendMode: 'screen', pointerEvents: 'none', animation: `glow_breathe ${g.dur}s ease-in-out infinite` }} />
          ))}
          {[
            { x: 476,  y: 164, dur: 3.4 }, { x: 608,  y: 276, dur: 4.8 }, { x: 838,  y: 272, dur: 3.9 },
            { x: 986,  y: 272, dur: 5.6 }, { x: 1150, y: 268, dur: 4.2 }, { x: 1396, y: 190, dur: 3.1 },
          ].map((e, i) => (
            <div key={`eye${i}`} style={{ position: 'absolute', left: e.x - 34, top: e.y - 26, width: 68, height: 52,
              background: 'radial-gradient(ellipse, rgba(210,90,255,0.55) 0%, transparent 70%)',
              mixBlendMode: 'screen', pointerEvents: 'none', animation: `eye_pulse ${e.dur}s ease-in-out infinite` }} />
          ))}
          <div style={{ position: 'absolute', left: -300, top: 240, width: 900, height: 420,
            background: 'radial-gradient(ellipse, rgba(28,10,44,0.55) 0%, transparent 65%)',
            pointerEvents: 'none', animation: 'smoke_a 58s ease-in-out infinite alternate' }} />
          <div style={{ position: 'absolute', left: 900, top: 300, width: 1000, height: 460,
            background: 'radial-gradient(ellipse, rgba(40,14,60,0.45) 0%, transparent 65%)',
            pointerEvents: 'none', animation: 'smoke_b 74s ease-in-out infinite alternate' }} />
          <div style={{ position: 'absolute', left: 50, top: 660, width: 1500, height: 300,
            background: 'radial-gradient(ellipse, rgba(205,70,255,0.38) 0%, transparent 68%)',
            mixBlendMode: 'screen', pointerEvents: 'none', animation: 'crack_pulse 6.8s ease-in-out infinite' }} />
          <div style={{ position: 'absolute', left: -60, top: -140, width: 620, height: 620,
            background: 'radial-gradient(circle, rgba(210,150,255,0.7) 0%, rgba(160,80,255,0.25) 45%, transparent 70%)',
            mixBlendMode: 'screen', pointerEvents: 'none', animation: 'flash_a 9s linear infinite' }} />
          <div style={{ position: 'absolute', left: 1090, top: -130, width: 560, height: 560,
            background: 'radial-gradient(circle, rgba(215,160,255,0.6) 0%, rgba(150,70,255,0.2) 45%, transparent 70%)',
            mixBlendMode: 'screen', pointerEvents: 'none', animation: 'flash_b 13s linear infinite' }} />
          <FloatingEmbers count={90} />
          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background:
            'linear-gradient(90deg, rgba(3,2,8,0.42) 0%, rgba(3,2,8,0.08) 22%, transparent 45%, transparent 60%, rgba(3,2,8,0.34) 100%)' }} />
        </div>

        {/* Boot blackout — the ONLY one. Covers the 16:9 stage; the letterbox
            bars outside are already solid black (wrap background), so together
            they cover every pixel of any window. The UI stage (with the logo)
            sits ABOVE this whole scene stage. */}
        {(entPhase === 'hold' || entPhase === 'fade' || entPhase === 'zoom') && (
          <div style={{ position: 'absolute', inset: 0, background: '#000', zIndex: 45, pointerEvents: 'none',
            opacity: entPhase === 'hold' ? 1 : 0,
            transition: entPhase === 'hold' ? 'none' : 'opacity 1.1s ease-in-out' }} />
        )}
      </div>
      </div>

      {/* ═══ UI STAGE — contain-scaled: every control always fully visible ═══ */}
      <div style={{ position: 'absolute', left: '50%', top: '50%', width: DW, height: DH, overflow: 'hidden',
        transform: `translate(-50%, -50%) scale(${scale})` }}>

        {view === 'home' && lineupVisible && (
          /* ══ V-formation lineup — all mounted, slid by transform ══ */
          <div style={{ position: 'absolute', left: 0, top: 0, width: DW, height: DH, pointerEvents: 'none',
            animation: entPhase === 'sweep' ? 'lineup_in 0.3s ease-out both' : undefined }}>
            <div style={{ position: 'absolute', left: 832, top: 806, transform: 'translateX(-50%)', width: 460, height: 84,
              background: 'radial-gradient(ellipse at center, rgba(180,77,255,0.34), rgba(255,140,30,0.18) 55%, transparent 72%)',
              filter: 'blur(6px)' }} />
            {ROSTER.map((c, i) => {
              let d = ((i - idx) % N + N) % N;      // 0..N-1
              if (d > N / 2) d -= N;                 // signed shortest distance
              const st = lineupStyle(d);
              const unlocked = charUnlocked(c.id);
              const showPose = d === 0 && lockedIn;
              return (
                <img key={c.id + (showPose ? '_pose' : '')}
                  src={resolveAsset(showPose ? `/characters/poses/${c.id}.png` : `/characters/hero/${c.id}.png`)} alt={c.name}
                  // 22.51b — was +34/step: back rows hovered way above the ground
                  // line (Dylan: "they look like they're floating"). 10px/step keeps
                  // a whisper of depth while every pair of feet stays near the dirt.
                  style={{ position: 'absolute', left: 832, bottom: 152 + (Math.abs(d) * 10), height: 600,
                    transformOrigin: 'bottom center',
                    transform: `translateX(calc(-50% + ${st.x}px)) scale(${st.scale})`,
                    opacity: st.op, zIndex: st.z,
                    filter: !unlocked
                      ? 'brightness(0.07) sepia(1) hue-rotate(230deg) saturate(3) drop-shadow(0 12px 18px rgba(0,0,0,0.6))'
                      : `brightness(${st.bright}) saturate(${d === 0 ? 1 : 0.55}) drop-shadow(0 14px 22px rgba(0,0,0,0.65))`,
                    transition: 'transform 0.22s cubic-bezier(0.25,0.9,0.3,1), filter 0.22s, opacity 0.22s, bottom 0.22s',
                    animation: showPose ? 'pose_pop 0.32s cubic-bezier(0.2,1.4,0.4,1) both' : undefined }} />
              );
            })}
          </div>
        )}

        {/* ══ START plate — one control, one position, mode-aware ══ */}
        {/* Landing smoke — a proper burst behind the duck when the sweep snaps
            home (Dylan: "more smoke"). Two rings: a dense low cloud at the feet
            plus taller rolling puffs, all staggered. */}
        {puffs && [
          // dense low ring at the feet
          { x: 742, y: 768, s: 130, d: 0 },   { x: 838, y: 792, s: 165, d: 15 },
          { x: 930, y: 770, s: 140, d: 30 },  { x: 690, y: 738, s: 100, d: 55 },
          { x: 980, y: 742, s: 110, d: 45 },  { x: 786, y: 748, s: 120, d: 25 },
          { x: 895, y: 752, s: 128, d: 10 },
          // taller rolling puffs up the body
          { x: 760, y: 672, s: 105, d: 90 },  { x: 912, y: 664, s: 112, d: 110 },
          { x: 820, y: 620, s: 95, d: 140 },  { x: 872, y: 700, s: 118, d: 70 },
          { x: 706, y: 690, s: 88, d: 160 },  { x: 958, y: 690, s: 92, d: 150 },
          { x: 836, y: 566, s: 78, d: 200 },
        ].map((p, i) => (
          <div key={i} style={{ position: 'absolute', left: p.x - p.s / 2, top: p.y - p.s / 2, width: p.s, height: p.s,
            borderRadius: '50%', zIndex: 25, pointerEvents: 'none',
            background: 'radial-gradient(circle, rgba(205,200,215,0.65) 0%, rgba(150,145,165,0.32) 45%, transparent 70%)',
            animation: `puff 0.9s ease-out ${p.d}ms both` }} />
        ))}

        {/* ══ SORRY BOSS logo — centered hero through the reveal AND the first
            beat of the survivor sweep, then a keyframed SLAP into the bottom-
            right corner: rear back, hurl across the screen, squash on impact,
            settle (Dylan: the plain shrink-to-corner was "mid"). Parks there
            permanently, clear of the caption strip (ends x=1210). */}
        <img src={SB_LOGO} alt="Sorry Boss"
          style={{
            position: 'absolute', pointerEvents: 'none',
            zIndex: logoBig ? 80 : 34,
            left: logoBig ? 440 : 1236,
            top: logoBig ? 168 : 630,
            width: logoBig ? 720 : 352,
            transform: logoBig ? 'none' : 'rotate(3deg)',
            filter: 'drop-shadow(0 10px 26px rgba(0,0,0,0.65))',
            animation: !logoBig && entPhase !== 'done'
              ? 'logo_fling 0.62s cubic-bezier(0.55,0,0.55,1) both' : undefined,
          }} />

        {/* BETA 22.51 — plate 452→390 wide (Dylan: "shorten it") — also clears
            the CO-OP LOBBY header, which starts at x=460 and was underlapping. */}
        <button onClick={tryStart}
          style={{ position: 'absolute', left: 46, top: 43, width: 390, height: 134, zIndex: 40,
            cursor: plateDisabled ? 'default' : 'pointer', border: 'none', padding: 0, borderRadius: 10, background: 'transparent', ...slam(0) }}
          onMouseEnter={e => { if (!plateDisabled) { e.currentTarget.style.filter = 'brightness(1.12)'; playMenuSound('ui_hover', 0.22); } }}
          onMouseLeave={e => (e.currentTarget.style.filter = 'none')}>
          <span style={{ position: 'absolute', inset: -5, borderRadius: 13, background: `linear-gradient(180deg, ${plateColors.frame} 0%, #0a0806 100%)`,
            boxShadow: '0 8px 20px rgba(0,0,0,0.6)' }} />
          <span style={{ position: 'absolute', inset: 0, borderRadius: 9, background: plateColors.face,
            boxShadow: 'inset 0 2px 0 rgba(255,255,255,0.18), inset 0 -8px 16px rgba(0,0,0,0.45)' }} />
          <span style={{ position: 'absolute', inset: 0, borderRadius: 9, backgroundImage: NOISE, opacity: 0.16, mixBlendMode: 'overlay', pointerEvents: 'none' }} />
          {[[16, 14], [362, 14], [16, 108], [362, 108]].map((p, i) => (
            <span key={i} style={{ position: 'absolute', left: p[0], top: p[1], width: 12, height: 12, borderRadius: '50%',
              background: 'radial-gradient(circle at 35% 30%, #d9dde3, #33383f 65%, #14171b)', boxShadow: '0 1px 2px rgba(0,0,0,0.7)' }} />
          ))}
          <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', gap: 20, paddingLeft: 34, color: plateColors.text }}>
            <span style={{ display: 'flex' }}><Icon d={heroUnlocked ? ICONS.skull : ICONS.lock} fill={heroUnlocked} size={heroUnlocked ? 60 : 44} /></span>
            <span>
              <span style={{ display: 'block', fontFamily: HEAVY, fontSize: 46, letterSpacing: '0.03em', lineHeight: 1.05,
                textShadow: '0 2px 0 rgba(255,255,255,0.18)' }}>{heroUnlocked ? 'START RUN' : 'LOCKED'}</span>
              <span style={{ display: 'block', fontFamily: MONO, fontSize: 12.5, letterSpacing: '0.14em', opacity: 0.85 }}>{plateColors.sub}</span>
            </span>
          </span>
        </button>

        {/* ══ Rail — Play / Prepare / System ══ */}
        <div style={{ position: 'absolute', left: 52, top: 205, display: 'flex', flexDirection: 'column', gap: 11, zIndex: 40, ...slam(50) }}>
          {[
            { key: 'solo', label: 'SOLO RUN', sub: 'your pace — picks pause the game', icon: ICONS.solo, active: view === 'home' },
            { key: 'coop', label: 'CO-OP · 1–4', sub: 'shared chaos — loot drops live', icon: ICONS.users, active: view === 'lobby' },
          ].map(t => (
            <button key={t.key} onClick={() => { if (!t.active) playMenuSound('ui_tab', 0.55); setView(t.key === 'solo' ? 'home' : 'lobby'); }}
              onMouseEnter={() => { setHoverNav(t.key); playMenuSound('ui_hover', 0.2); }} onMouseLeave={() => setHoverNav(h => (h === t.key ? null : h))}
              style={{ position: 'relative', width: 300, height: 64, display: 'flex', alignItems: 'center', gap: 16,
                paddingLeft: 22, cursor: 'pointer', borderRadius: 8, textAlign: 'left',
                background: t.active ? 'linear-gradient(180deg, #40332a 0%, #241b14 100%)' : PLATE_BG,
                border: `2px solid ${t.active ? '#ff8c3a' : hoverNav === t.key ? '#c8542a' : '#05070b'}`,
                outline: '1px solid rgba(150,170,195,0.14)', outlineOffset: -4,
                boxShadow: t.active ? '0 0 18px rgba(255,140,58,0.35), inset 0 1px 0 rgba(255,220,180,0.12)' : PLATE_SHADOW(hoverNav === t.key),
                color: t.active ? '#ffd9b5' : '#dde4ee', transition: 'border-color 0.12s, box-shadow 0.12s' }}>
              <Screw x={7} y={7} /><Screw x={287} y={7} /><Screw x={7} y={51} /><Screw x={287} y={51} />
              <Grime radius={6} opacity={0.12} />
              <span style={{ display: 'flex', color: t.active ? '#ff9a4d' : '#c3ccd9' }}><Icon d={t.icon} size={26} /></span>
              <span>
                <span style={{ display: 'block', fontFamily: HEAVY, fontSize: 23, letterSpacing: '0.06em', lineHeight: 1.1,
                  textShadow: '0 2px 2px rgba(0,0,0,0.7)' }}>{t.label}</span>
                <span style={{ display: 'block', fontFamily: MONO, fontSize: 10, letterSpacing: '0.05em', color: t.active ? '#d9a06a' : '#79828f' }}>{t.sub}</span>
              </span>
            </button>
          ))}
          <div style={{ height: 1, background: 'rgba(150,170,195,0.16)', margin: '3px 8px' }} />
          {NAV_PREP.map(n => (
            <button key={n.key} onClick={() => onNav(n.key)}
              onMouseEnter={() => { setHoverNav(n.key); playMenuSound('ui_hover', 0.2); }} onMouseLeave={() => setHoverNav(h => (h === n.key ? null : h))}
              style={{ position: 'relative', width: 264, height: 54, display: 'flex', alignItems: 'center', gap: 18,
                paddingLeft: 24, cursor: 'pointer', borderRadius: 8, textAlign: 'left', background: PLATE_BG,
                border: `2px solid ${hoverNav === n.key ? '#c8542a' : '#05070b'}`,
                outline: '1px solid rgba(150,170,195,0.14)', outlineOffset: -4,
                boxShadow: PLATE_SHADOW(hoverNav === n.key),
                color: hoverNav === n.key ? '#ffd9b5' : '#dde4ee', transition: 'border-color 0.12s, color 0.12s' }}>
              <Grime radius={6} opacity={0.12} />
              <span style={{ display: 'flex', color: hoverNav === n.key ? '#ff9a4d' : '#c3ccd9' }}><Icon d={n.icon} size={24} /></span>
              <span style={{ fontFamily: HEAVY, fontSize: 22, letterSpacing: '0.07em', textShadow: '0 2px 2px rgba(0,0,0,0.7)' }}>{n.label}</span>
            </button>
          ))}
          <div style={{ height: 1, background: 'rgba(150,170,195,0.16)', margin: '3px 8px' }} />
          {NAV_SYS.map(n => (
            <button key={n.key} onClick={() => onNav(n.key)}
              onMouseEnter={() => { setHoverNav(n.key); playMenuSound('ui_hover', 0.2); }} onMouseLeave={() => setHoverNav(h => (h === n.key ? null : h))}
              style={{ position: 'relative', width: 264, height: 46, display: 'flex', alignItems: 'center', gap: 18,
                paddingLeft: 24, cursor: 'pointer', borderRadius: 8, textAlign: 'left', background: PLATE_BG,
                border: `2px solid ${hoverNav === n.key ? '#c8542a' : '#05070b'}`,
                outline: '1px solid rgba(150,170,195,0.14)', outlineOffset: -4,
                boxShadow: PLATE_SHADOW(hoverNav === n.key),
                color: hoverNav === n.key ? '#ffd9b5' : '#c8d0dc', transition: 'border-color 0.12s, color 0.12s' }}>
              <Grime radius={6} opacity={0.12} />
              <span style={{ display: 'flex', color: hoverNav === n.key ? '#ff9a4d' : '#a7b1bf' }}><Icon d={n.icon} size={21} /></span>
              <span style={{ fontFamily: HEAVY, fontSize: 19, letterSpacing: '0.07em' }}>{n.label}</span>
            </button>
          ))}
        </div>

        {/* ══ Player plate (identity + real lifetime progression) ══ */}
        <div style={{ position: 'absolute', left: 52, top: 752, width: 348, height: 44, borderRadius: 8, display: 'flex', zIndex: 40,
          alignItems: 'center', gap: 12, paddingLeft: 16, background: 'rgba(10,8,14,0.72)', border: '1px solid rgba(150,170,195,0.16)', ...slam(210) }}>
          <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.1em', color: '#8b93a1' }}>PLAYER</span>
          <span style={{ fontFamily: HEAVY, fontSize: 17, color: '#ffd9b5' }}>{(signedIn ? (username || 'SURVIVOR') : 'GUEST').toUpperCase()}</span>
          <span style={{ fontFamily: MONO, fontSize: 11, color: '#8b93a1' }}>
            {signedIn
              ? <>BEST LV {stats?.highestLevelReached ?? 1} · {(stats?.totalKills ?? 0).toLocaleString()} KILLS</>
              : <>sign in to save your progress</>}
          </span>
        </div>

        {/* ══ Discord + News ══ */}
        <div style={{ position: 'absolute', left: 52, top: 808, display: 'flex', gap: 14, zIndex: 40, ...slam(240) }}>
          {[{ k: 'discord', label: 'DISCORD' }, { k: 'news', label: 'NEWS' }].map(b => (
            <button key={b.k} onClick={() => { playMenuSound('ui_select', 0.5); comingSoon(b.label); }}
              onMouseEnter={() => playMenuSound('ui_hover', 0.2)}
              style={{ position: 'relative', height: 48, padding: '0 20px', display: 'flex', alignItems: 'center', gap: 10,
                cursor: 'pointer', borderRadius: 8, background: PLATE_BG, border: '2px solid #05070b',
                outline: '1px solid rgba(150,170,195,0.14)', outlineOffset: -4, boxShadow: PLATE_SHADOW(false),
                color: '#c9d2de', fontFamily: HEAVY, fontSize: 17, letterSpacing: '0.06em' }}>
              <Grime radius={6} opacity={0.12} />
              <Icon d={ICONS.chat} size={20} />{b.label}
            </button>
          ))}
          {/* BETA 22.52 — SIGN IN plate (signed-out only): ember accent, lives
              with Discord/News. Browsing is free; this is the polite door. */}
          {!signedIn && onRequestSignIn && (
            <button onClick={() => { playMenuSound('ui_select', 0.5); onRequestSignIn(); }}
              onMouseEnter={() => playMenuSound('ui_hover', 0.2)}
              style={{ position: 'relative', height: 48, padding: '0 18px', display: 'flex', alignItems: 'center', gap: 9,
                cursor: 'pointer', borderRadius: 8, background: PLATE_BG, border: '2px solid #05070b',
                outline: '1px solid rgba(255,140,58,0.35)', outlineOffset: -4, boxShadow: PLATE_SHADOW(false),
                color: '#ffb066', fontFamily: HEAVY, fontSize: 16, letterSpacing: '0.06em' }}>
              <Grime radius={6} opacity={0.12} />
              <Icon d={ICONS.solo} size={18} />SIGN IN
            </button>
          )}
          {/* BETA 22.51 — SIGN OUT lives here now (stage-anchored), replacing the
              viewport-fixed top-right bar that floated off the 16:9 box and could
              cover the co-op lobby's ✕. Muted styling — a system action, not a CTA. */}
          {onSignOut && (
            <button onClick={() => { playMenuSound('ui_select', 0.5); onSignOut(); }}
              onMouseEnter={() => playMenuSound('ui_hover', 0.2)}
              style={{ position: 'relative', height: 48, padding: '0 16px', display: 'flex', alignItems: 'center', gap: 8,
                cursor: 'pointer', borderRadius: 8, background: PLATE_BG, border: '2px solid #05070b',
                outline: '1px solid rgba(150,170,195,0.14)', outlineOffset: -4, boxShadow: PLATE_SHADOW(false),
                color: '#8b93a1', fontFamily: HEAVY, fontSize: 15, letterSpacing: '0.06em' }}>
              <Grime radius={6} opacity={0.12} />
              <Icon d={ICONS.power} size={17} />SIGN OUT
            </button>
          )}
        </div>

        {view === 'home' && (<>
          {/* ══ Overhead title + name ══ */}
          <div style={{ position: 'absolute', left: 560, top: 34, width: 560, textAlign: 'center', pointerEvents: 'none', zIndex: 35, ...slam(90) }}>
            <div style={{ fontFamily: MONO, fontSize: 14, letterSpacing: '0.34em', color: '#cfc4e6', textShadow: '0 2px 4px rgba(0,0,0,0.9)' }}>
              CHOOSE YOUR SURVIVOR
            </div>
            <div key={hero.id} style={{ fontFamily: HEAVY, fontSize: 72, lineHeight: 1.04, color: heroUnlocked ? PURPLE : '#6d6585',
              textShadow: '0 3px 6px rgba(0,0,0,0.9), 0 0 30px rgba(180,77,255,0.4)', animation: 'name_in 0.25s ease-out' }}>
              {hero.name}
            </div>
          </div>

          {/* LB/RB pills — visual signposts (clickable for mouse, never focus targets) */}
          <button onClick={() => cycle(-1)} aria-label="previous"
            style={{ position: 'absolute', left: 520, top: 470, cursor: 'pointer', zIndex: 40,
              background: 'rgba(10,8,14,0.7)', border: '1px solid rgba(180,77,255,0.4)', borderRadius: 8, padding: '8px 14px',
              color: '#cfa9f2', fontFamily: MONO, fontSize: 13, letterSpacing: '0.1em', ...slam(130) }}>
            ◂ LB
          </button>
          <button onClick={() => cycle(1)} aria-label="next"
            style={{ position: 'absolute', left: 1064, top: 470, cursor: 'pointer', zIndex: 40,
              background: 'rgba(10,8,14,0.7)', border: '1px solid rgba(180,77,255,0.4)', borderRadius: 8, padding: '8px 14px',
              color: '#cfa9f2', fontFamily: MONO, fontSize: 13, letterSpacing: '0.1em', ...slam(130) }}>
            RB ▸
          </button>

          {/* roster dots — filled = unlocked, hollow = locked */}
          <div style={{ position: 'absolute', left: 560, top: 662, width: 560, display: 'flex', justifyContent: 'center', gap: 9, zIndex: 40, ...slam(150) }}>
            {ROSTER.map((c, i) => {
              const unlocked = charUnlocked(c.id);
              const active = i === idx;
              return (
                <span key={c.id} style={{ width: active ? 11 : 8, height: active ? 11 : 8, borderRadius: '50%', marginTop: active ? 0 : 1.5,
                  background: unlocked ? (active ? PURPLE : 'rgba(180,77,255,0.55)') : 'transparent',
                  border: unlocked ? 'none' : '1.5px solid rgba(180,77,255,0.45)',
                  boxShadow: active ? '0 0 8px rgba(180,77,255,0.7)' : 'none', transition: 'all 0.15s' }} />
              );
            })}
          </div>

          {/* ══ LOCK IN — the choose-your-survivor commit (Dylan 22.63) ══ */}
          {view === 'home' && heroUnlocked && (
            <button onClick={toggleLockIn}
              onMouseEnter={() => playMenuSound('ui_hover', 0.2)}
              style={{ position: 'absolute', left: 832, top: 596, transform: 'translateX(-50%)', zIndex: 41,
                width: lockedIn ? 168 : 196, height: 44, borderRadius: 8, cursor: 'pointer',
                background: lockedIn ? 'linear-gradient(180deg, #274a22 0%, #142a10 100%)' : 'linear-gradient(180deg, #6e1f16 0%, #3d0f0a 100%)',
                border: `2px solid ${lockedIn ? '#69c04a' : '#c8542a'}`,
                boxShadow: lockedIn ? '0 0 16px rgba(105,192,74,0.35)' : '0 4px 10px rgba(0,0,0,0.55)',
                fontFamily: HEAVY, fontSize: 19, letterSpacing: '0.08em',
                color: lockedIn ? '#c9f0b8' : '#f3e9da', transition: 'all 0.16s', ...slam(140) }}>
              {lockedIn ? '\u2713 LOCKED IN' : 'LOCK IN'}
            </button>
          )}

          {/* ══ Caption strip — borderless lower-third scrim ══ */}
          <div style={{ position: 'absolute', left: 460, top: 700, width: 750, height: 150, zIndex: 40,
            background: 'linear-gradient(180deg, rgba(6,4,10,0) 0%, rgba(6,4,10,0.72) 26%, rgba(6,4,10,0.9) 100%)',
            borderRadius: 10, display: 'flex', alignItems: 'flex-end', paddingBottom: 16, ...slam(120) }}>
            <div style={{ flex: 1.02, paddingLeft: 30, display: 'flex', alignItems: 'center', gap: 16 }}>
              <img src={weaponSrc} alt="" style={{ width: 118, height: 66, objectFit: 'contain',
                filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.8))', flexShrink: 0 }} />
              <div style={{ textAlign: 'left', minWidth: 0 }}>
                <div style={{ fontFamily: MONO, fontSize: 11.5, letterSpacing: '0.16em', color: '#98a0ae', display: 'flex', alignItems: 'center', gap: 6 }}>
                  STARTING WEAPON <span style={{ display: 'inline-flex', color: '#c8a44a' }}><Icon d={ICONS.lock} size={12} /></span>
                </div>
                <div style={{ fontFamily: HEAVY, fontSize: 23, color: '#f2f4f8', marginTop: 2, textShadow: '0 2px 2px rgba(0,0,0,0.8)' }}>{hero.weapon}</div>
                <div style={{ fontFamily: MONO, fontSize: 10.5, color: '#79828f', marginTop: 2 }}>always equipped — mod it in the bench</div>
              </div>
            </div>
            <div style={{ width: 1, height: 96, background: 'rgba(255,255,255,0.12)', marginBottom: 6 }} />
            <div style={{ flex: 1, paddingLeft: 24, paddingRight: 26, display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 52, height: 58, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
                background: heroUnlocked ? 'linear-gradient(180deg, rgba(120,50,190,0.6), rgba(60,20,100,0.6))' : 'rgba(40,36,56,0.7)',
                border: `2px solid ${heroUnlocked ? 'rgba(180,77,255,0.6)' : 'rgba(120,110,150,0.4)'}` }}>
                <span style={{ fontFamily: HEAVY, fontSize: 24, color: heroUnlocked ? '#e3c8ff' : '#7a7195' }}>{hero.name.charAt(0)}</span>
              </div>
              <div style={{ textAlign: 'left', minWidth: 0 }}>
                <div style={{ fontFamily: MONO, fontSize: 11.5, letterSpacing: '0.16em', color: '#98a0ae' }}>
                  {heroUnlocked ? 'PASSIVE' : 'HOW TO UNLOCK'}
                </div>
                {heroUnlocked ? (<>
                  <div style={{ fontFamily: HEAVY, fontSize: 23, color: PURPLE, marginTop: 2,
                    textShadow: '0 0 14px rgba(180,77,255,0.35)' }}>{(pass?.signatureName ?? '—').toUpperCase()}</div>
                  <div style={{ fontFamily: MONO, fontSize: 12.5, lineHeight: 1.45, color: '#dfe3ec', marginTop: 2 }}>
                    {passDesc.map((part, i) => /\d/.test(part)
                      ? <span key={i} style={{ color: PURPLE, fontWeight: 700 }}>{part}</span>
                      : <span key={i}>{part}</span>)}
                  </div>
                </>) : (<>
                  <div style={{ fontFamily: HEAVY, fontSize: 21, color: '#e8b64f', marginTop: 2 }}>LOCKED</div>
                  <div style={{ fontFamily: MONO, fontSize: 12.5, color: '#dfe3ec', marginTop: 2 }}>{hero.unlockReq || 'Keep playing to unlock.'}</div>
                </>)}
              </div>
            </div>
          </div>

          {/* ══ Daily Objective (top-right, from Dylan's approved mockup) ══ */}
          <div style={{ position: 'absolute', left: 1272, top: 53, width: 284, borderRadius: 10, padding: '18px 20px 16px', zIndex: 35,
            background:
              'radial-gradient(ellipse 70% 50% at 85% -10%, rgba(140,80,220,0.14), transparent 60%),' +
              'linear-gradient(180deg, rgba(30,20,42,0.92) 0%, rgba(14,9,22,0.93) 100%)',
            border: '2px solid #08060d', outline: '1px solid rgba(160,110,255,0.16)', outlineOffset: -4,
            boxShadow: 'inset 0 2px 0 rgba(190,150,255,0.10), 0 5px 14px rgba(0,0,0,0.55)', ...slam(170) }}>
            <Screw x={7} y={7} /><Screw x={269} y={7} />
            <Grime radius={8} opacity={0.10} />
            <div style={{ fontFamily: HEAVY, fontSize: 20, letterSpacing: '0.05em', color: PURPLE,
              textShadow: '0 0 12px rgba(180,77,255,0.4)' }}>DAILY OBJECTIVE</div>
            <div style={{ fontFamily: MONO, fontSize: 16, color: '#eef1f7', margin: '8px 0' }}>Kill 2000 enemies</div>
            <div style={{ position: 'relative', height: 22, borderRadius: 4, background: '#0a0712',
              border: '1px solid #35284e', boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.8)', overflow: 'hidden', marginBottom: 12 }}>
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: MONO, fontSize: 12, color: '#cfc2e8', letterSpacing: '0.08em' }}>0 / 2000</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontFamily: HEAVY, fontSize: 15, letterSpacing: '0.08em', color: PURPLE }}>REWARD</span>
              <span style={{ fontFamily: MONO, fontSize: 14, color: '#eef1f7' }}>1,000 XP</span>
              <span style={{ fontFamily: MONO, fontSize: 14, color: '#ffcf4d', display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 13, height: 13, borderRadius: '50%', background: 'radial-gradient(circle at 35% 30%, #ffe79a, #d99b16)', display: 'inline-block' }} />750
              </span>
            </div>
          </div>

          {/* button legend — the tutorial that isn't one */}
          <div style={{ position: 'absolute', left: 1040, top: 868, width: 410, textAlign: 'right', fontFamily: MONO, fontSize: 11.5,
            letterSpacing: '0.08em', color: '#79828f', zIndex: 40, ...slam(260) }}>
            ← → CHANGE SURVIVOR &nbsp;·&nbsp; ENTER START RUN
          </div>
        </>)}

        {view === 'lobby' && (<>
          <div style={{ position: 'absolute', left: 460, top: 24, display: 'flex', alignItems: 'baseline', gap: 14, zIndex: 35 }}>
            <span style={{ fontFamily: HEAVY, fontSize: 40, color: PURPLE, transform: 'rotate(-1deg)', display: 'inline-block',
              textShadow: '0 2px 0 rgba(60,10,90,0.9), 0 0 22px rgba(180,77,255,0.45)' }}>💀 CO-OP LOBBY</span>
          </div>
          <div style={{ position: 'absolute', left: 464, top: 82, fontFamily: MONO, fontSize: 13, letterSpacing: '0.22em', color: '#cfc4e6', zIndex: 35 }}>
            UP TO 4 PLAYERS ✦ {p1Ready ? '1/1 READY' : '0/1 READY'}
          </div>
          <button onClick={() => { playMenuSound('ui_back', 0.45); setView('home'); }} aria-label="close lobby"
            style={{ position: 'absolute', right: 44, top: 26, width: 50, height: 50, cursor: 'pointer', borderRadius: 8, zIndex: 40,
              fontFamily: HEAVY, fontSize: 24, color: PURPLE, background: 'linear-gradient(180deg, #201c2b 0%, #131019 100%)',
              border: '2px solid rgba(168,85,247,0.45)', boxShadow: '0 0 12px rgba(168,85,247,0.3)' }}>✕</button>

          {Array.from({ length: 4 }, (_, i) => {
            const x = 452 + i * 232;
            if (i === 0) {
              const lvl = stats?.highestLevelReached ?? 1;
              return (
                <div key={i} style={{ position: 'absolute', left: x, top: 128, width: 220, height: 620, borderRadius: 10, zIndex: 35,
                  background: 'linear-gradient(180deg, rgba(24,18,10,0.94) 0%, rgba(10,8,6,0.95) 100%)',
                  border: '2px solid #eab308', boxShadow: '0 0 18px rgba(234,179,8,0.25)', padding: '10px 12px', textAlign: 'center' }}>
                  <div style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.18em', color: '#eab308' }}>PLAYER 1 — YOU · HOST</div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 6 }}>
                    <button onClick={() => cycle(-1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#eab308', fontSize: 22, padding: '0 4px' }}>‹</button>
                    <span style={{ fontFamily: HEAVY, fontSize: 23, color: heroUnlocked ? '#f5d060' : '#8a8060' }}>{hero.name}</span>
                    <button onClick={() => cycle(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#eab308', fontSize: 22, padding: '0 4px' }}>›</button>
                  </div>
                  <div style={{ height: 205, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', marginTop: 4 }}>
                    <img key={hero.id} src={heroSrc} alt="" style={{ maxHeight: 200, maxWidth: 185, objectFit: 'contain',
                      filter: heroUnlocked ? 'drop-shadow(0 8px 12px rgba(0,0,0,0.7))' : 'brightness(0.15)' }} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                    <span style={{ fontFamily: MONO, fontSize: 10.5, color: '#0d0a06', background: '#eab308', borderRadius: 3, padding: '2px 6px' }}>LVL {lvl}</span>
                    <span style={{ flex: 1, height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.10)', overflow: 'hidden' }}>
                      <span style={{ display: 'block', width: `${Math.min(100, lvl * 4)}%`, height: '100%', background: '#eab308' }} />
                    </span>
                  </div>
                  <div style={{ textAlign: 'left', marginTop: 12 }}>
                    <div style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: '0.14em', color: '#98a0ae' }}>STARTING WEAPON</div>
                    <div style={{ fontFamily: HEAVY, fontSize: 16, color: '#f2f4f8', marginTop: 2 }}>{hero.weapon}</div>
                    <div style={{ height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={weaponSrc} alt="" style={{ maxWidth: 170, maxHeight: 42, objectFit: 'contain', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.8))' }} />
                    </div>
                    <div style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: '0.14em', color: '#98a0ae', marginTop: 4 }}>PASSIVE</div>
                    <div style={{ fontFamily: HEAVY, fontSize: 15, color: PURPLE, marginTop: 2 }}>{(pass?.signatureName ?? '—').toUpperCase()}</div>
                    <div style={{ fontFamily: MONO, fontSize: 10.5, lineHeight: 1.45, color: '#cfd3dc', marginTop: 3 }}>{pass?.signatureDesc ?? ''}</div>
                  </div>
                  <button onClick={() => {
                    if (!heroUnlocked) { playMenuSound('ui_denied', 0.6); say(`LOCKED — ${hero.unlockReq}`); return; }
                    // field-radio ack going ready, descending stand-down coming off
                    toggleReady();
                  }}
                    style={{ position: 'absolute', left: 12, right: 12, bottom: 10, height: 42, cursor: 'pointer', borderRadius: 7,
                      fontFamily: HEAVY, fontSize: 18, letterSpacing: '0.06em',
                      color: p1Ready ? '#c9f0c2' : '#f5d060',
                      background: p1Ready ? 'linear-gradient(180deg, #274a26 0%, #17301a 100%)' : 'linear-gradient(180deg, #4a3a12 0%, #2c220a 100%)',
                      border: `2px solid ${p1Ready ? 'rgba(110,220,110,0.5)' : 'rgba(234,179,8,0.5)'}` }}>
                    {p1Ready ? '✓ READY' : 'READY UP'}
                  </button>
                </div>
              );
            }
            return (
              <div key={i} style={{ position: 'absolute', left: x, top: 128, width: 220, height: 620, borderRadius: 10, zIndex: 35,
                background: 'rgba(12,10,18,0.55)', border: '2px dashed #35304a', padding: '10px 12px', textAlign: 'center' }}>
                <div style={{ fontFamily: MONO, fontSize: 11, letterSpacing: '0.18em', color: '#6d6585' }}>PLAYER {i + 1}</div>
                <div style={{ fontSize: 46, color: '#3d3654', marginTop: 190 }}>+</div>
                <div style={{ fontFamily: HEAVY, fontSize: 20, color: '#7a7195', marginTop: 8 }}>EMPTY SLOT</div>
                <div style={{ fontFamily: MONO, fontSize: 11, color: '#5d5573', marginTop: 6, lineHeight: 1.5 }}>
                  invite a friend
                  <br /><span style={{ color: '#4a4360' }}>online co-op in development</span>
                </div>
              </div>
            );
          })}

          <div style={{ position: 'absolute', left: 1392, top: 128, width: 164, borderRadius: 10, padding: '14px 14px', zIndex: 35,
            background: 'linear-gradient(180deg, rgba(30,20,42,0.94) 0%, rgba(14,9,22,0.95) 100%)', border: '2px solid #08060d',
            outline: '1px solid rgba(160,110,255,0.16)', outlineOffset: -4 }}>
            <div style={{ fontFamily: HEAVY, fontSize: 15, color: PURPLE, marginBottom: 8 }}>LOBBY SETTINGS</div>
            {[['PLAYERS', '1–4'], ['DIFFICULTY', 'NORMAL'], ['FRIENDLY FIRE', 'OFF'], ['VOICE', 'SOON'], ['CROSSPLAY', 'SOON']].map(([k, vv]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontFamily: MONO, fontSize: 10, color: '#cfd3dc', padding: '4px 0' }}>
                <span>{k}</span><span style={{ color: vv === 'SOON' ? '#5d5573' : '#efe9f8' }}>{vv}</span>
              </div>
            ))}
            <div style={{ height: 1, background: 'rgba(160,110,255,0.18)', margin: '10px 0' }} />
            <div style={{ fontFamily: HEAVY, fontSize: 14, color: PURPLE }}>MUTATORS</div>
            <div style={{ fontFamily: MONO, fontSize: 10, color: '#5d5573', marginTop: 6, lineHeight: 1.5 }}>None — in development.</div>
          </div>

          <div style={{ position: 'absolute', left: 460, top: 790, fontFamily: MONO, fontSize: 12, letterSpacing: '0.12em', color: '#5d5573', zIndex: 35 }}>
            LOBBY CODE: <span style={{ color: '#7a7195' }}>—— coming soon ——</span> &nbsp;·&nbsp; invites open when online co-op lands
          </div>
        </>)}

        {/* ══ Version badge + patch notes ══ */}
        <button onClick={() => { playMenuSound('ui_select', 0.45); setShowChangelog(true); }}
          style={{ position: 'absolute', left: 1466, top: 862, padding: '4px 10px', cursor: 'pointer',
            background: 'rgba(8,4,2,0.78)', border: '1px solid rgba(255,140,26,0.5)', borderRadius: 3,
            color: '#ff8c1a', fontSize: 11, fontFamily: MONO, letterSpacing: '0.1em', zIndex: 45, ...slam(280) }}>
          ● {CURRENT_VERSION}
        </button>
        {showChangelog && (
          <div style={{ position: 'absolute', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.78)' }}
            onClick={() => { playMenuSound('ui_back', 0.45); setShowChangelog(false); }}>
            <div data-native-scroll="true" onClick={e => e.stopPropagation()}
              style={{ background: 'rgba(14,8,6,0.98)', border: '1px solid rgba(255,140,26,0.4)', borderRadius: 3, padding: '22px 26px',
                maxWidth: 420, width: '90%', fontFamily: MONO, color: 'rgba(245,232,220,0.9)', maxHeight: '65vh', overflowY: 'auto',
                touchAction: 'pan-y', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
              <div style={{ color: '#ff8c1a', fontSize: '0.82rem', letterSpacing: '0.2em', marginBottom: 18, textAlign: 'center' }}>● PATCH NOTES</div>
              {/* key includes the index — the changelog has repeated version
                  strings (several "BETA 13.3x" entries), which spammed React
                  duplicate-key errors every time patch notes opened. */}
              {CHANGELOG.map((entry, ei) => (
                <div key={`${entry.version}-${ei}`} style={{ marginBottom: 20 }}>
                  <div style={{ color: '#ff8c1a', fontSize: '0.75rem', letterSpacing: '0.12em', marginBottom: 8,
                    borderBottom: '1px solid rgba(255,140,26,0.25)', paddingBottom: 4 }}>{entry.version} — {entry.date}</div>
                  {(entry.highlights ?? []).map((c, i) => (
                    <div key={i} style={{ fontSize: '0.68rem', color: 'rgba(235,222,205,0.85)', marginBottom: 4, paddingLeft: 10 }}>+ {c}</div>
                  ))}
                  {entry.fixes && entry.fixes.length > 0 && (
                    <>
                      <div style={{ fontSize: '0.62rem', color: 'rgba(220,205,190,0.4)', letterSpacing: '0.1em', marginTop: 8, marginBottom: 4 }}>FIXES</div>
                      {entry.fixes.map((c, i) => (
                        <div key={i} style={{ fontSize: '0.68rem', color: 'rgba(235,222,205,0.65)', marginBottom: 4, paddingLeft: 10 }}>✕ {c}</div>
                      ))}
                    </>
                  )}
                </div>
              ))}
              <div style={{ textAlign: 'center', marginTop: 6 }}>
                <button onClick={() => setShowChangelog(false)} style={{ background: 'transparent', border: '1px solid rgba(255,140,26,0.35)',
                  color: 'rgba(255,140,26,0.75)', fontFamily: MONO, fontSize: '0.65rem', letterSpacing: '0.15em', padding: '5px 20px', cursor: 'pointer', borderRadius: 2 }}>
                  [ CLOSE ]
                </button>
              </div>
            </div>
          </div>
        )}

        {toast && (
          <div style={{ position: 'absolute', left: '50%', top: 590, transform: 'translateX(-50%)', zIndex: 100,
            fontFamily: HEAVY, fontSize: 19, letterSpacing: '0.08em', color: '#ffd7b0',
            background: 'rgba(10,7,16,0.92)', border: '2px solid #c8542a', borderRadius: 8, padding: '10px 24px',
            boxShadow: '0 0 18px rgba(255,106,42,0.35)' }}>{toast}</div>
        )}
      </div>

      <style>{`
        /* Menu Feel v1 — every plate physically sinks under the finger. Scoped
           to .sbmenu so in-game buttons are untouched. */
        .sbmenu button:active { transform: translateY(1px) translateZ(0); }
        /* BETA 22.61 — Dylan: "words on the buttons increase in size on hover
           / off hover — I just want the text to stay big." The menu sits in a
           transform-scaled stage; on hover repaints Chromium re-rasterized
           each button's text at a different effective resolution, so the
           label visibly popped between two sizes. Promoting every button to
           its OWN compositing layer pins its rasterization — the text renders
           at the sharp size permanently and never re-rasters on hover. The
           :active press also no longer scales (translateY nudge only). */
        .sbmenu button { transform: translateZ(0); backface-visibility: hidden; }
        @keyframes name_in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pose_pop { 0% { opacity: 0.4; } 45% { opacity: 1; } 100% { opacity: 1; } }
        @keyframes lineup_in { from { opacity: 0; } to { opacity: 1; } }
        @keyframes puff {
          0%   { opacity: 0; transform: scale(0.25); }
          20%  { opacity: 1; }
          100% { opacity: 0; transform: scale(2.0) translateY(-26px); }
        }
        /* UI drops from way above the frame — top-down slam (Dylan). */
        @keyframes ui_slam {
          0%   { opacity: 0; transform: translateY(-190px); }
          60%  { opacity: 1; transform: translateY(10px); }
          78%  { transform: translateY(-4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        /* The logo's comic slap into the corner: rear back, hurl, squash on
           impact, settle at the sticker's resting tilt. End frame == the
           element's base sticker style, so there's zero pop when it finishes. */
        @keyframes logo_fling {
          0%   { left: 440px;  top: 168px; width: 720px; transform: rotate(0deg); }
          16%  { left: 452px;  top: 146px; width: 738px; transform: rotate(-5deg); }
          62%  { left: 1244px; top: 616px; width: 352px; transform: rotate(9deg); }
          76%  { left: 1228px; top: 640px; width: 368px; transform: rotate(-1deg) scaleY(0.88); }
          88%  { left: 1240px; top: 626px; width: 346px; transform: rotate(5deg); }
          100% { left: 1236px; top: 630px; width: 352px; transform: rotate(3deg); }
        }
        @keyframes bg_drift { from { transform: scale(1) translate(0, 0); } to { transform: scale(1.045) translate(-0.7%, -0.5%); } }
        @keyframes glow_breathe { 0%, 100% { opacity: 0.35; } 50% { opacity: 0.95; } }
        @keyframes eye_pulse { 0%, 100% { opacity: 0.18; } 50% { opacity: 0.85; } }
        @keyframes crack_pulse { 0%, 100% { opacity: 0.30; } 50% { opacity: 0.78; } }
        @keyframes smoke_a { from { transform: translateX(0) translateY(0); } to { transform: translateX(340px) translateY(-30px); } }
        @keyframes smoke_b { from { transform: translateX(0) translateY(0); } to { transform: translateX(-380px) translateY(24px); } }
        @keyframes flash_a { 0%, 88% { opacity: 0; } 89% { opacity: 0.75; } 90.5% { opacity: 0.12; } 92% { opacity: 0.5; } 94% { opacity: 0; } 100% { opacity: 0; } }
        @keyframes flash_b { 0%, 93% { opacity: 0; } 94% { opacity: 0.65; } 95.5% { opacity: 0.1; } 96.5% { opacity: 0.42; } 98% { opacity: 0; } 100% { opacity: 0; } }
      `}</style>
    </div>
  );
}
